import React, { forwardRef } from 'react'
import Rating from './Rating.tsx'
import AddToCartButton from './AddToCartButton.tsx'
import { formatPriceWithLocale } from '@aytm/app/swidget/base/questions/Ecommerce/functions.ts'
import { getMediumAssetPath } from '@aytm/lib/common/assets.ts'
import { IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '@aytm/app/swidget/base/questions/Ecommerce/types'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale.ts'
import style from './Card.module.scss'
import standartStyle from '../Standart.module.scss'

interface ICardProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  quantity: number
  findability: boolean
  disabled?: boolean
  index: number
  applyCustomWidth?: boolean
  onItemSelect: (index: number) => void
  onAddToCart: (qnt: -1 | 1, index: number) => void
}

const Card = forwardRef<HTMLDivElement, ICardProps>(
  (
    { schema, answer, quantity, findability, onAddToCart, index, onItemSelect, disabled, applyCustomWidth = false },
    ref
  ) => {
    const viewport = useViewport()
    const { t, languageTag } = useSurveyLocale()
    return (
      <div
        ref={ref}
        className={`
        ${style.card}
        ${viewport.mobile ? style.cardMobile : ''}
        ${viewport.mobile && 2 === schema.products_per_row_mobile ? style.cardMobile2PerRow : ''}
        ${disabled ? style.disabled : ''}
        ${
          applyCustomWidth && null !== schema.products_per_row && !viewport.mobile
            ? style[`card${schema.products_per_row}PerRow`]
            : ''
        }
      `}
      >
        <div className={style.image} onClick={() => onItemSelect(index)}>
          {answer.image ? (
            <div
              className={style.mainImage}
              role="img"
              aria-label={answer.image?.alt_text || t('questions', 'img', 'noAlt')}
              style={{ backgroundImage: `url(${getMediumAssetPath(answer.image)})` }}
            />
          ) : (
            <div className={style.noImage} />
          )}
        </div>
        <div className={style.content}>
          <div className={style.redLabel} dangerouslySetInnerHTML={{ __html: answer.red_label }} />
          <div
            className={style.caption}
            dangerouslySetInnerHTML={{ __html: answer.text }}
            onClick={() => onItemSelect(index)}
          />
          <div className={style.ratingWrapper}>{answer.rating > 0 && <Rating rating={answer.rating} />}</div>
          <div className={`${style.priceWrapper} ${standartStyle.prices}`}>
            {answer.discounted_price && answer.price > answer.discounted_price ? (
              <>
                <div
                  className={standartStyle.newPrice}
                  dangerouslySetInnerHTML={{
                    __html: `${formatPriceWithLocale(answer.discounted_price, schema.price_mask, languageTag, true, false)}`
                  }}
                />
                <div
                  className={standartStyle.lastPrice}
                  dangerouslySetInnerHTML={{
                    __html: `${formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)}`
                  }}
                />
                <div
                  className={standartStyle.off}
                  dangerouslySetInnerHTML={{
                    __html: t<(off: string, applied: boolean) => string>(
                      'questions',
                      'ec',
                      'ecommerce',
                      'off'
                    )(
                      (
                        ((schema.answers[index].price - schema.answers[index].discounted_price) /
                          schema.answers[index].price) *
                        100
                      ).toFixed(1),
                      false
                    )
                  }}
                />
              </>
            ) : (
              <div
                className={standartStyle.defaultPrice}
                dangerouslySetInnerHTML={{
                  __html: `${formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)}`
                }}
              />
            )}
          </div>
          <div className={style.buttonContainer}>
            <AddToCartButton
              quantity={quantity}
              findability={findability}
              index={index}
              size={viewport.mobile && 2 === schema.products_per_row_mobile ? 'full' : 'small'}
              onAddToCart={onAddToCart}
              limit={schema.max_qty_per_product}
            />
          </div>
        </div>
      </div>
    )
  }
)

Card.displayName = 'Card'
export default Card
